import { denverEvening } from "@/lib/schedule";

/* Sample data for the in-house club prototype (/blue-2/members). Everything
   here is invented for layout review: member names, comments, posts, class
   titles and makes. Replace with real data when the backend exists. Photos
   reuse images already on the site. */

export type Role = "visitor" | "friend" | "member" | "chelsea";
export const ROLES: { key: Role; label: string; note: string }[] = [
  { key: "visitor", label: "Visitor", note: "Not signed in" },
  { key: "friend", label: "Thread Friend", note: "$3/mo · no classes" },
  { key: "member", label: "Member", note: "$25/mo · everything" },
  { key: "chelsea", label: "Chelsea", note: "Creator view" },
];
/** What each role can open. Thread Friends get posts but not classes or lives. */
export const can = (role: Role, what: "posts" | "classes" | "live" | "community" | "manage") =>
  role === "chelsea" || (role === "member" && what !== "manage") || (role === "friend" && (what === "posts" || what === "community"));

export type Member = { id: string; name: string; avatar?: string; since: string; badge?: "founding" | "chelsea" };
export const CHELSEA: Member = { id: "chelsea", name: "Chelsea", avatar: "/img/chelsea.jpeg", since: "2024", badge: "chelsea" };

/** The one label for the founding badge, everywhere it shows. */
export const FOUNDING = "Founding member";

export type Plan = "friend" | "member";
/** Club tiers: price per month and name. Copy always writes prices as "$25/mo". */
export const PLANS: Record<Plan, { name: string; price: number; per: string }> = {
  friend: { name: "Thread Friend", price: 3, per: "$3/mo" },
  member: { name: "Member", price: 25, per: "$25/mo" },
};

/** The signed-in sample person: a founding member who came over from Patreon.
    Use this on every page (greeting, comment author, chat name, account card)
    so the prototype always shows the same person. For the Thread Friend review
    role use `viewer("friend")`, which is the same person on the $3/mo plan. */
export const ME: Member & {
  first: string; initials: string; founding: boolean; email: string; joined: string; no: string;
  patreonSince: string; plan: Plan; nextBilling: string;
} = {
  id: "me", name: "Katie B.", first: "Katie", initials: "KB", since: "2025", badge: "founding", founding: true,
  email: "katie.b@example.com", joined: "March 2025", no: "0042", patreonSince: "March 2025",
  plan: "member", nextBilling: "Nov 3, 2026",
};
export type Viewer = typeof ME;
/** Who is signed in for a review role: null for visitors, ME (as a Thread Friend
    or Member) for friend/member, and Chelsea (with ME's extra fields filled in)
    for the creator view. */
export function viewer(role: Role): Viewer | null {
  if (role === "visitor") return null;
  if (role === "friend") return { ...ME, plan: "friend" };
  if (role === "member") return ME;
  return { ...ME, ...CHELSEA, first: "Chelsea", initials: "C", founding: false, email: "chelsea@example.com", no: "0001", joined: "2024", patreonSince: "2019" };
}
export const MEMBERS: Member[] = [
  { id: "m1", name: "Sam R.", since: "2025", badge: "founding" },
  { id: "m2", name: "Jo P.", since: "2025" },
  { id: "m3", name: "Lee K.", since: "2026" },
  { id: "m4", name: "Mara T.", since: "2025", badge: "founding" },
  { id: "m5", name: "Dana W.", since: "2026" },
  { id: "m6", name: "Priya S.", since: "2026" },
];

export type Project = { id: string; title: string; month: string; blurb: string; image: string; skill: "Beginner" | "Confident beginner" | "Intermediate"; supplies: string[]; pattern?: string };
export const PROJECTS: Project[] = [
  { id: "tote", title: "Patchwork Tote", month: "October 2026", blurb: "A roomy patchwork tote you'll reach for every day. Pick your fabrics and sew along.", image: "/chelsea.JPG", skill: "Confident beginner",
    supplies: ["1 yd main fabric", "Fat quarter bundle (6)", "1 yd fusible fleece", "Webbing for straps", "Matching thread"], pattern: "Patchwork Tote (PDF, included)" },
  { id: "vest", title: "Quilted Vest", month: "September 2026", blurb: "A cozy quilted vest from a quilt you love, or one you make for it.", image: "/concept/gingham-top.jpg", skill: "Intermediate",
    supplies: ["Lap quilt or 2 yds quilted fabric", "Double-fold bias tape", "Buttons or snaps"] },
  { id: "pouch", title: "Zipper Pouch Trio", month: "August 2026", blurb: "Three sizes, zero zipper tears. The pouch class that makes zippers click.", image: "/img/products/qs-mat.jpg", skill: "Beginner",
    supplies: ["Fat quarters", "3 zippers (7\", 9\", 12\")", "Fusible interfacing"] },
];

export type Chapter = { t: string; label: string };
export type ClassVideo = { id: string; projectId: string; title: string; part: string; date: string; length: string; poster: string; chapters: Chapter[]; watched?: number };
export const CLASSES: ClassVideo[] = [
  { id: "tote-1", projectId: "tote", title: "Cutting & piecing the panels", part: "Class 1 of 2", date: "Oct 13, 2026", length: "1:32:10", poster: "/video/live-class.jpg",
    chapters: [{ t: "0:00", label: "Hello + fabric show-and-tell" }, { t: "9:40", label: "Cutting the squares" }, { t: "31:15", label: "Piecing the front panel" }, { t: "58:02", label: "Pressing seams" }, { t: "1:12:30", label: "Your questions" }] },
  { id: "tote-2", projectId: "tote", title: "Straps, lining & finishing", part: "Class 2 of 2", date: "Oct 15, 2026", length: "1:28:44", poster: "/video/live-class.jpg",
    chapters: [{ t: "0:00", label: "Catch-up" }, { t: "6:20", label: "Making the straps" }, { t: "34:00", label: "Lining" }, { t: "1:05:10", label: "Topstitching + reveal" }] },
  { id: "vest-1", projectId: "vest", title: "Cutting your quilt", part: "Class 1 of 2", date: "Sep 8, 2026", length: "1:24:03", poster: "/chelsea-rainbow-portrait.jpg", watched: 1,
    chapters: [{ t: "0:00", label: "Choosing a quilt" }, { t: "18:30", label: "Pattern layout" }, { t: "44:00", label: "Cutting" }] },
  { id: "vest-2", projectId: "vest", title: "Binding & buttons", part: "Class 2 of 2", date: "Sep 10, 2026", length: "1:30:51", poster: "/chelsea-rainbow-portrait.jpg", watched: 0.42,
    chapters: [{ t: "0:00", label: "Assembly" }, { t: "40:12", label: "Binding" }, { t: "1:10:00", label: "Buttons" }] },
  { id: "pouch-1", projectId: "pouch", title: "Zippers without tears", part: "Class 1 of 2", date: "Aug 11, 2026", length: "1:18:27", poster: "/chelsea-and-cosmo.jpg", watched: 1,
    chapters: [{ t: "0:00", label: "Zipper anatomy" }, { t: "22:00", label: "First pouch" }] },
];

/* ─── Class timing ───
   Sample classes carry a calendar date; each one airs live at 7 PM Denver
   time (see @/lib/schedule) and its replay exists only once that live is over.
   Every page uses these so tonight's class never shows up as a replay. */

/** How long a club live runs, for deciding when its replay exists. */
export const LIVE_MS = 2 * 36e5;
/** When a class goes live: 7 PM Denver on its date. */
export const classStart = (c: ClassVideo) => { const d = new Date(c.date); return denverEvening(d.getFullYear(), d.getMonth(), d.getDate()); };
/** True once the class's live has finished (start + 2 h). */
export const aired = (c: ClassVideo, now = new Date()) => classStart(c).getTime() + LIVE_MS <= now.getTime();
/** True when a class has a watchable replay. Same as aired(); use this name in replay lists and "replay is up" copy. */
export const hasReplay = (c: ClassVideo, now = new Date()) => aired(c, now);
/** Where a class is right now: "replay" (aired), "live" (on air), or "upcoming". */
export function classStatus(c: ClassVideo, now = new Date()): "replay" | "live" | "upcoming" {
  const t = classStart(c).getTime(), n = now.getTime();
  return n >= t + LIVE_MS ? "replay" : n >= t ? "live" : "upcoming";
}
/** Newest class first, by air time. Use with Array.sort. */
export const byNewest = (a: ClassVideo, b: ClassVideo) => classStart(b).getTime() - classStart(a).getTime();
/** The classes that have replays now, newest first. */
export const replays = (now = new Date(), list: ClassVideo[] = CLASSES) => list.filter(c => hasReplay(c, now)).sort(byNewest);

export type Comment = { id: string; by: Member; text: string; ago: string; likes: number; replies?: Comment[] };
export type Post = {
  id: string; kind: "update" | "photo" | "poll" | "project" | "replay"; visibility: "public" | "friends" | "members";
  title: string; body: string; images?: string[]; ago: string; likes: number; pinned?: boolean;
  poll?: { q: string; options: { label: string; votes: number }[] }; classId?: string; projectId?: string; comments: Comment[];
};
const [sam, jo, lee, mara, dana, priya] = MEMBERS;
export const POSTS: Post[] = [
  { id: "p1", kind: "project", visibility: "public", pinned: true, title: "October project: the Patchwork Tote!", projectId: "tote",
    body: "This month we're making a roomy patchwork tote. Class 1 is Tuesday the 13th, class 2 is Thursday the 15th, both 7 PM MT. Supply list and pattern are in the class page. Pull your favorite scraps!", images: ["/chelsea.JPG"], ago: "5 days ago", likes: 48,
    comments: [
      { id: "c1", by: sam, text: "Already pulling fabric. Is a fat quarter bundle enough for the front?", ago: "5d", likes: 6, replies: [{ id: "c1r", by: CHELSEA, text: "Yes! Six fat quarters covers the front and back panels.", ago: "5d", likes: 9 }] },
      { id: "c2", by: mara, text: "Finally a tote class 🙌", ago: "4d", likes: 3 },
    ] },
  { id: "p3", kind: "poll", visibility: "members", title: "Help me pick November's project", body: "I can't decide. You choose!", ago: "2 days ago", likes: 22,
    poll: { q: "November project", options: [{ label: "Holiday table runner", votes: 41 }, { label: "Cozy flannel pajama pants", votes: 63 }, { label: "Mini quilt wall hanging", votes: 28 }] }, comments: [{ id: "c4", by: dana, text: "PAJAMA PANTS. Pajamas encouraged, right?", ago: "2d", likes: 14 }] },
  { id: "p4", kind: "photo", visibility: "friends", title: "Studio Sunday", body: "Cosmo approved today's fabric pull. Thank you for supporting the fun, Thread Friends!", images: ["/chelsea-and-cosmo.jpg"], ago: "4 days ago", likes: 57,
    comments: [{ id: "c5", by: priya, text: "Cosmo for president", ago: "4d", likes: 20 }] },
  { id: "p5", kind: "update", visibility: "members", title: "Members: 15% off the tote kit", body: "Code CLUBTOTE at the fabric shop through Oct 31. Members only, please don't share!", ago: "1 week ago", likes: 19, comments: [] },
  // A replay post for a class that has already aired (see hasReplay): tonight's
  // tote class gets its "replay is up" post the morning after, not before.
  { id: "p2", kind: "replay", visibility: "members", title: "Replay is up: Binding & buttons", classId: "vest-2", body: "Thanks for sewing vests with me! The replay has chapters now, so you can jump straight to the binding.", ago: "Sep 11", likes: 31,
    comments: [{ id: "c3", by: lee, text: "The binding trick at 40:12 changed my life.", ago: "Sep 11", likes: 12 }] },
];

export type Make = { id: string; by: Member; title: string; image: string; projectId?: string; likes: number; challenge?: string };
export const MAKES: Make[] = [
  { id: "k1", by: sam, title: "My first vest!", image: "/concept/gingham-top.jpg", projectId: "vest", likes: 34 },
  { id: "k2", by: mara, title: "Pouches for my sisters", image: "/img/products/qs-mat.jpg", projectId: "pouch", likes: 21, challenge: "Gift-it-forward" },
  { id: "k3", by: jo, title: "Label obsession", image: "/concept/label.jpg", likes: 18 },
  { id: "k4", by: priya, title: "Rainbow scrap pile progress", image: "/chelsea-rainbow-portrait.jpg", likes: 27, challenge: "Scrap-busting September" },
  { id: "k5", by: lee, title: "Tote panel done!", image: "/chelsea.JPG", projectId: "tote", likes: 15 },
  { id: "k6", by: dana, title: "Studio helper", image: "/chelsea-and-cosmo.jpg", likes: 40 },
];
export const CHALLENGE = { title: "Scrap-busting October", blurb: "Use only scraps for one tote panel. Post it by Oct 31 to be entered to win a fat quarter bundle.", ends: "Oct 31", entries: 23 };

export const CHAT = [
  { by: sam, text: "hi from Ohio! 👋", t: "7:01" },
  { by: CHELSEA, text: "Hi Sam!! Grab your squares, we're cutting first", t: "7:02" },
  { by: mara, text: "is 1/4\" seam allowance on everything?", t: "7:09" },
  { by: CHELSEA, text: "Yes, 1/4\" throughout!", t: "7:09" },
  { by: jo, text: "COSMO CAMEO 🐈", t: "7:14" },
  { by: lee, text: "first zipper I haven't cried over", t: "7:20" },
];

/** Comments on a post (or a list of comments), counting replies at every depth.
    Comments flagged `hidden` (Chelsea's moderation) are skipped unless
    `withHidden` is set, which the creator view uses. */
type Countable = { hidden?: boolean; replies?: Countable[] };
export function countComments(from: { comments: Countable[] } | Countable[], withHidden = false): number {
  const list = Array.isArray(from) ? from : from.comments;
  return list.reduce((n, c) => (c.hidden && !withHidden ? n : n + 1 + countComments(c.replies ?? [], withHidden)), 0);
}
